import type { IconNames } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { v4 as uuid } from 'uuid';

import {
  ConditionOutcome,
  FlowNodeTypes,
} from 'components/powerFlowShared/components/types';
import { formatPortsHelper } from 'components/powerFlowShared/helpers/nodePortFormatter';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import { PowerCallFlowExtraDataT } from '../PowerCallFlow';
import { Locations as ActionModal } from '../modals/locations';

export type Payload = {
  non_match_node_id?: string;
  rules: RulePayload[];
};

export type RulePayload = {
  match_node_id?: string;
  matchings: MatchingPayload[];
  name: string;
  rule_type: 'any';
};

export type MatchingPayload = {
  match: string;
  rule: 'prefix';
  type: 'full-number';
};

export type FlowCallNodeLocations = NodeT<FlowNodeTypes.LOCATIONS, Payload>;

const portTranslator = (node: FlowCallNodeLocations): RegisterPortT[] => {
  const rules: RegisterPortT[] = node.payload.rules.map(
    (rule: RulePayload) => ({
      connection: rule.match_node_id || uuid(),
      id: uuid(),
      label: rule.name,
      type: 'out',
    }),
  );

  return [
    {
      connection: node.payload.non_match_node_id || uuid(),
      id: uuid(),
      label: ConditionOutcome.NO_MATCH,
      type: 'out',
    },
    ...rules,
  ];
};

export class LocationsNode extends NodeTypeComponent<
  FlowCallNodeLocations,
  PowerCallFlowExtraDataT
>() {
  static icon: IconNames = 'world';
  static label = 'Locations';
  static helpText =
    'Sort incoming calls based on country code. This is useful for general and specific locations (e.g. +44 for UK or +49035 for Dresden in Germany).';
  static color = flowNodeColor.purple;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.LOCATIONS };
  static updateConnection = (
    node: FlowCallNodeLocations,
    port: InternalPortT,
    newConnection: string,
  ): FlowCallNodeLocations => {
    const payload = { ...node.payload };

    if (port.connection === payload.non_match_node_id) {
      payload.non_match_node_id = newConnection;
    } else {
      const index = payload.rules.findIndex(
        ({ match_node_id }) => port.connection === match_node_id,
      );

      if (index >= 0) {
        payload.rules[index].match_node_id = newConnection;
      }
    }

    return { ...node, payload };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    const { rules } = this.props.node.payload;

    return (
      <FlowNode
        color={LocationsNode.color}
        icon={LocationsNode.icon}
        label={LocationsNode.label}
        text={
          <strong>
            {rules.length} {rules.length === 1 ? 'location' : 'locations'}
          </strong>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
